import { useState } from "react";
import type { ContactGroup, Contact } from "../types";
import { GROUP_COLORS } from "../types";

interface Props {
  groups: ContactGroup[];
  contacts: Contact[];
  selectedGroupId: string | null;
  onSelectGroup: (id: string | null) => void;
  onAddGroup: (g: Omit<ContactGroup, "id" | "createdAt">) => void;
  onEditGroup: (g: ContactGroup) => void;
  onDeleteGroup: (id: string) => void;
  compact?: boolean;
}

interface GroupFormState {
  name: string;
  color: string;
  description: string;
}

const DEFAULT_FORM: GroupFormState = { name: "", color: "violet", description: "" };

export default function GroupPanel({
  groups,
  contacts,
  selectedGroupId,
  onSelectGroup,
  onAddGroup,
  onEditGroup,
  onDeleteGroup,
  compact = false,
}: Props) {
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<ContactGroup | null>(null);
  const [form, setForm] = useState<GroupFormState>(DEFAULT_FORM);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const getCount = (gid: string) => contacts.filter((c) => c.groupIds.includes(gid)).length;
  const getColorCfg = (color: string) => GROUP_COLORS.find((c) => c.value === color) ?? GROUP_COLORS[0];

  const openAdd = () => {
    setEditTarget(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  };

  const openEdit = (g: ContactGroup) => {
    setEditTarget(g);
    setForm({ name: g.name, color: g.color, description: g.description ?? "" });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    if (editTarget) {
      onEditGroup({ ...editTarget, name: form.name.trim(), color: form.color, description: form.description.trim() });
    } else {
      onAddGroup({ name: form.name.trim(), color: form.color, description: form.description.trim() });
    }
    setShowForm(false);
    setForm(DEFAULT_FORM);
    setEditTarget(null);
  };

  // ── Compact mode: horizontal scrollable chips ──
  if (compact) {
    return (
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-2.5 overflow-x-auto scrollbar-none">
          {/* 전체 */}
          <button
            onClick={() => onSelectGroup(null)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all shrink-0 border ${
              selectedGroupId === null
                ? "bg-gradient-to-r from-violet-600/30 to-pink-600/20 border-violet-500/40 text-white"
                : "bg-white/[0.04] border-white/[0.08] text-gray-400 hover:text-gray-200"
            }`}
          >
            <i className="ri-contacts-book-2-line text-xs"></i>
            전체
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              selectedGroupId === null ? "bg-white/20 text-white" : "bg-white/[0.06] text-gray-500"
            }`}>
              {contacts.length}
            </span>
          </button>

          {/* Groups */}
          {groups.map((g) => {
            const cfg = getColorCfg(g.color);
            const count = getCount(g.id);
            const isActive = selectedGroupId === g.id;
            return (
              <button
                key={g.id}
                onClick={() => onSelectGroup(g.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all shrink-0 border ${
                  isActive
                    ? `${cfg.bg} ${cfg.text} ${cfg.border}`
                    : "bg-white/[0.04] border-white/[0.08] text-gray-400 hover:text-gray-200"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} shrink-0`}></span>
                {g.name}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  isActive ? "bg-white/20" : "bg-white/[0.06] text-gray-500"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}

          {/* Add button */}
          <button
            onClick={openAdd}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.12] cursor-pointer transition-all shrink-0"
            title="그룹 추가"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>

        {/* Inline add/edit form */}
        {showForm && (
          <div className="border-t border-white/[0.08] p-3 space-y-2 bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="그룹 이름"
                maxLength={20}
                autoFocus
                className="flex-1 min-w-0 bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
              />
              <div className="flex items-center gap-1">
                {GROUP_COLORS.slice(0, 4).map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setForm({ ...form, color: c.value })}
                    className={`w-5 h-5 flex items-center justify-center rounded-full cursor-pointer transition-all ${c.bg} ${
                      form.color === c.value ? "ring-2 ring-white/40 scale-110" : "hover:scale-105"
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${c.dot}`}></span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSubmit}
                disabled={!form.name.trim()}
                className="flex-1 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-40 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
              >
                {editTarget ? "저장" : "추가"}
              </button>
              <button
                onClick={() => { setShowForm(false); setEditTarget(null); }}
                className="px-3 py-2 rounded-lg bg-white/[0.06] text-gray-400 hover:text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
              >
                취소
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Desktop mode: vertical sidebar panel ──
  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.07]">
        <div>
          <h3 className="text-white font-semibold text-sm">그룹</h3>
          <p className="text-gray-500 text-xs mt-0.5">{groups.length}개</p>
        </div>
        <button
          onClick={openAdd}
          className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-600/40 to-pink-600/30 text-violet-300 hover:text-white cursor-pointer transition-all hover:scale-105"
          title="그룹 추가"
        >
          <i className="ri-add-line text-base"></i>
        </button>
      </div>

      {/* Group list */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* All contacts */}
        <button
          onClick={() => onSelectGroup(null)}
          className={`w-full flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all ${
            selectedGroupId === null
              ? "bg-gradient-to-r from-violet-600/20 to-pink-600/10 border-r-2 border-violet-500"
              : "hover:bg-white/[0.04]"
          }`}
        >
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.07] shrink-0">
            <i className="ri-contacts-book-2-line text-gray-400 text-sm"></i>
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className={`text-sm font-medium truncate ${selectedGroupId === null ? "text-white" : "text-gray-300"}`}>
              전체 연락처
            </p>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
            selectedGroupId === null ? "bg-violet-500/30 text-violet-300" : "bg-white/[0.06] text-gray-500"
          }`}>
            {contacts.length}
          </span>
        </button>

        {/* Groups */}
        {groups.map((g) => {
          const cfg = getColorCfg(g.color);
          const count = getCount(g.id);
          const isActive = selectedGroupId === g.id;
          return (
            <div key={g.id} className="group/item relative">
              <button
                onClick={() => onSelectGroup(g.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-violet-600/20 to-pink-600/10 border-r-2 border-violet-500"
                    : "hover:bg-white/[0.04]"
                }`}
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${cfg.bg} shrink-0`}>
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className={`text-sm font-medium truncate ${isActive ? "text-white" : "text-gray-300"}`}>
                    {g.name}
                  </p>
                  {g.description && (
                    <p className="text-gray-600 text-xs truncate">{g.description}</p>
                  )}
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                  isActive ? `${cfg.bg} ${cfg.text}` : "bg-white/[0.06] text-gray-500"
                }`}>
                  {count}
                </span>
              </button>

              {/* Hover actions */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden group-hover/item:flex items-center gap-1 bg-[#0d0d1a] rounded-lg p-0.5">
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(g); }}
                  className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-violet-400 cursor-pointer transition-colors rounded"
                  title="수정"
                >
                  <i className="ri-pencil-line text-xs"></i>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(g.id); }}
                  className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-red-400 cursor-pointer transition-colors rounded"
                  title="삭제"
                >
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="border-t border-white/[0.08] p-4 space-y-3 bg-white/[0.02]">
          <p className="text-white text-xs font-semibold">{editTarget ? "그룹 수정" : "새 그룹 추가"}</p>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="그룹 이름"
            maxLength={20}
            autoFocus
            className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
          />
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="설명 (선택)"
            maxLength={40}
            className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
          />
          {/* Color picker */}
          <div className="flex items-center gap-2 flex-wrap">
            {GROUP_COLORS.map((c) => (
              <button
                key={c.value}
                onClick={() => setForm({ ...form, color: c.value })}
                className={`w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-all ${c.bg} ${
                  form.color === c.value ? "ring-2 ring-white/40 scale-110" : "hover:scale-105"
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`}></span>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={!form.name.trim()}
              className="flex-1 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-40 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            >
              {editTarget ? "저장" : "추가"}
            </button>
            <button
              onClick={() => { setShowForm(false); setEditTarget(null); }}
              className="px-3 py-2 rounded-lg bg-white/[0.06] text-gray-400 hover:text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirmId && (
        <div className="border-t border-white/[0.08] p-4 bg-red-500/[0.05]">
          <p className="text-white text-xs font-semibold mb-1">그룹을 삭제할까요?</p>
          <p className="text-gray-500 text-xs mb-3">연락처는 삭제되지 않습니다.</p>
          <div className="flex gap-2">
            <button
              onClick={() => { onDeleteGroup(deleteConfirmId); setDeleteConfirmId(null); }}
              className="flex-1 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            >
              삭제
            </button>
            <button
              onClick={() => setDeleteConfirmId(null)}
              className="px-3 py-2 rounded-lg bg-white/[0.06] text-gray-400 hover:text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}